import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
    getMIOpen,
} from '@atredux/selectors/atAlertSelector'

import Button from '@amzn/meridian/button'
import Column from '@amzn/meridian/column'
import Modal, { ModalFooter } from '@amzn/meridian/modal'
import Pagination from '@amzn/meridian/pagination'
import Row from '@amzn/meridian/row'
import Select, { SelectOption } from '@amzn/meridian/select'
import Table, { TableRow, TableCell } from '@amzn/meridian/table'
import Text from '@amzn/meridian/text'
import { useBundle } from '@amzn/react-arb-tools'

import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { MasterIndirectTaskService } from '@amzn/ihm-associate-tools-js-lib'
import { getSelectedTool } from '@src/redux/selectors/toolSelector'
import { resetMI } from '@src/redux/slices/atAlertSlice'
import { switchTool } from '@atredux/slices/toolSlice'
import { getToolNameToDetailMap, seawardReactTool } from '@services/ToolMenuConfigService'
import { _navigateToTool } from './SideBar/utils/SideBarUtil'
import { IdleTimeService } from '@src/services/IdleTimeService'

// Not translated: a MasterIndirectTasks config value compared against a task's
// directTasks list, never rendered.
export const ALL_TOOLS = 'All Tools'
const ITEMS_PER_PAGE = 5

const MIModal = (): React.ReactElement => {
    const [selectedTask, setSelectedTask] = useState(MasterIndirectTaskService.getInstance().getMITask())
    const [bundle] = useBundle(BUNDLE_NAME)
    const dispatch = useDispatch()
    const tool = useSelector(getSelectedTool)
    const open: boolean = useSelector(getMIOpen)
    const [currentPage, setCurrentPage] = useState(1)
    let miConfig = MasterIndirectTaskService.getInstance().getConfig()

    const closeMI = useCallback(() => {
        dispatch(resetMI())
    }, [selectedTask, dispatch])

    // update config with the latest version on every render
    useEffect(() => {
        miConfig = MasterIndirectTaskService.getInstance().getConfig()
    }, [])

    const setSelectedTaskAndRestPage = (task: string) => {
        setSelectedTask(task)
        setCurrentPage(1)
    }

    const setTask = useCallback(() => {
        if (
            !miConfig.MasterIndirectTasksList.tasks[selectedTask]?.directTasks.includes(tool.displayName) &&
            !miConfig.MasterIndirectTasksList.tasks[selectedTask]?.directTasks.includes(ALL_TOOLS)
        ) {
            MasterIndirectTaskService.getInstance().setMITask(selectedTask)
            _navigateToTool(seawardReactTool)
            MasterIndirectTaskService.getInstance().setPureEvent(true)
            dispatch(resetMI())
        } else {
            MasterIndirectTaskService.getInstance().setMITask(selectedTask)
            dispatch(resetMI())
        }
        const promptThreshold = miConfig.MasterIndirectTasksList.tasks[selectedTask]?.idlePromptThreshold
        const totThreshold = miConfig.MasterIndirectTasksList.tasks[selectedTask]?.timeOffThreshold
        IdleTimeService.getInstance().init(promptThreshold, totThreshold)
    }, [dispatch, selectedTask, tool])

    useEffect(() => {
        if (open) {
            IdleTimeService.getInstance().disablePrompt()
        }
    }, [open])

    let tasks
    if (miConfig) {
        tasks = Object.keys(miConfig.MasterIndirectTasksList.tasks)
        tasks.sort()
    }
    const directTasks = miConfig?.MasterIndirectTasksList.tasks[selectedTask]?.directTasks
    if (directTasks) {
        directTasks.sort()
    }

    const numberOfPages = Math.ceil(directTasks?.length / ITEMS_PER_PAGE)
    const firstVisibleIndex = (currentPage - 1) * ITEMS_PER_PAGE
    const lastVisibleIndex = firstVisibleIndex + ITEMS_PER_PAGE

    return (
        <Modal
            title={t(bundle, 'jsma-web-masterTask-title')}
            open={open}
            onClose={closeMI}
            scrollContainer="viewport"
            closeLabel={t(bundle, 'jsma-web-common-close')}
            width="75vw"
        >
            <Column alignmentVertical={'justify'} height="100%">
                {directTasks &&
                    <Column>
                        <Row>
                            <Text>{t(bundle, 'jsma-web-masterTask-instructions')}</Text>
                        </Row>
                        <Row>
                            <Table showDividers={true}>
                                {
                                    directTasks?.slice(firstVisibleIndex, lastVisibleIndex).map((task) => {
                                        return (
                                            <TableRow key={task} onClick={() => {
                                                MasterIndirectTaskService.getInstance().setMITask(selectedTask)
                                                _navigateToTool(getToolNameToDetailMap().get(task))
                                                dispatch(resetMI())
                                            }}>
                                                <TableCell>{task}</TableCell>
                                            </TableRow>
                                        )
                                    })
                                }
                            </Table>
                        </Row>
                        <Pagination
                            showSkipArrows={true}
                            numberOfPages={numberOfPages}
                            onChange={setCurrentPage}
                            currentPage={currentPage}
                        />
                    </Column>
                }
                <Column alignmentVertical={'bottom'} alignmentHorizontal={'center'}>
                    <Row>
                        <Select
                            placeholder={t(bundle, 'jsma-web-masterTask-selectPlaceholder')}
                            value={selectedTask}
                            onChange={setSelectedTaskAndRestPage}
                            width="90vw"
                        >
                            {
                                tasks?.map((task) => {
                                    return <SelectOption value={task} label={task} key={task}/>
                                })
                            }
                        </Select>
                    </Row>
                </Column>
            </Column>
            <ModalFooter>
                <Row alignmentHorizontal="end" widths="fit">
                    <Button type="secondary" size="small" onClick={closeMI}>
                        {t(bundle, 'migrated-ihm-seaward-bluetooth-cancel-button')}
                    </Button>
                    <Button type="primary" size="small" onClick={setTask}>
                        {t(bundle, 'jsma-web-common-confirm')}
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    )
}

export default MIModal
